import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { BlogArticle, BlogCategory } from "@/types/blog";
import {
  getArticlePath,
  getRecentBlogArticles,
  searchBlogArticles,
} from "@/apis/blog-service";
import { formatDisplayDate } from "@/utility";
import { useLanguageContext } from "@/hooks";

type BlogSidebarProps = {
  categories: BlogCategory[];
  currentArticle?: BlogArticle;
};

export const BlogSidebar: React.FC<BlogSidebarProps> = ({ categories, currentArticle }) => {
  const { language } = useLanguageContext();
  const [query, setQuery] = useState("");
  const searchResults = useMemo(() => searchBlogArticles(query).slice(0, 5), [query]);
  const recentArticles = getRecentBlogArticles(4).filter((article) => article.slug !== currentArticle?.slug);
  const searchArticlesLabel = language("label.search_articles") || "Search articles";
  const categoriesLabel = language("label.categories") || "Categories";
  const recentArticlesLabel = language("label.recent_articles") || "Recent articles";

  return (
    <aside className="sticky-lg-top" style={{ top: "1rem" }}>
      <div className="d-flex flex-column gap-4">
        <section className="border rounded-3 p-3">
          <h2 className="h6 mb-3">{searchArticlesLabel}</h2>
          <label className="visually-hidden" htmlFor="blog-sidebar-search">
            {searchArticlesLabel}
          </label>
          <input
            id="blog-sidebar-search"
            className="form-control"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={searchArticlesLabel}
          />
          {query && (
            <div className="d-flex flex-column gap-2 mt-3">
              {searchResults.length > 0 ? (
                searchResults.map((article) => (
                  <Link className="text-decoration-none link small" to={getArticlePath(article)} key={article.slug}>
                    {article.title}
                  </Link>
                ))
              ) : (
                <span className="text-muted small">{language("label.no_articles_found") || "No articles found."}</span>
              )}
            </div>
          )}
        </section>

        <section className="border rounded-3 p-3">
          <h2 className="h6 mb-3">{categoriesLabel}</h2>
          <div className="d-flex flex-column gap-2">
            {categories.map((category) => (
              <Link to={`/blog/${category.categorySlug}`} className="text-decoration-none kg-green" key={category.categorySlug}>
                {category.categoryName} <i className="bi bi-arrow-right kg-green"></i>
              </Link>
            ))}
          </div>
        </section>

        <section className="border rounded-3 p-3">
          <h2 className="h6 mb-3">{recentArticlesLabel}</h2>
          <div className="d-flex flex-column gap-3">
            {recentArticles.map((article) => (
              <Link to={getArticlePath(article)} className="text-decoration-none link" key={article.slug}>
                <span className="d-block fw-semibold small">{article.title}</span>
                {article.date && <span className="d-block text-muted small">{formatDisplayDate(article.date)}</span>}
              </Link>
            ))}
          </div>
        </section>
      </div>
    </aside>
  );
};

export default BlogSidebar;
